/**
 * Responsibility: turn a stack of horizontal rings into line segments.
 *
 * ## Why this is its own module
 *
 * It was written inside `buildingShapes.js`, where the idea came from: an archetype declares its
 * *solids* — one or more stacks of rings, bottom to top — and both the drawn lines and the depth mask are
 * derived from that one description, so a mask can never disagree with the shape it is masking.
 *
 * The theme park needs exactly the same thing for tents, carousel roofs and drums, and copying it would
 * put the derivation in two places. A big top and a setback tower have nothing in common as *shapes*, but
 * "a ring stack becomes these lines" is one rule with one authoritative home.
 *
 * Contains no Three.js and no DOM, so it is fully unit testable.
 */

/** Tolerance for two points being the same place, in world units. Far below anything visible. */
const COINCIDENT = 1e-9;

/**
 * A segment record, in the shape every emitter in the project consumes.
 *
 * `t1` and `t2` are height fractions used for the colour gradient, not positions.
 */
export function segmentAt(x1, y1, z1, x2, y2, z2, t1, t2) {
  return { x1, y1, z1, x2, y2, z2, t1, t2 };
}

/**
 * Whether two points are the same place, so a zero-length segment is never emitted.
 *
 * Not fussiness: a tapered shape whose top ring collapses to a point, or two rings that happen to
 * coincide, would otherwise contribute invisible lines that still cost buffer space and a draw.
 */
export function samePoint(a, b) {
  return (
    Math.abs(a[0] - b[0]) < COINCIDENT &&
    Math.abs(a[1] - b[1]) < COINCIDENT &&
    Math.abs(a[2] - b[2]) < COINCIDENT
  );
}

/** Height fraction of an absolute Y within a shape, for the colour gradient. */
export function fractionAt(y, baseY, height) {
  return height > 0 ? (y - baseY) / height : 0;
}

/**
 * Corners of a rectangle, optionally rotated about its centre.
 *
 * Winding matches `structureGeometry.emitBox` — (-x,-z), (+x,-z), (+x,+z), (-x,+z) — so a shape built
 * here and a box built there agree about which corner is which.
 */
export function rectFootprint(centerX, centerZ, width, depth, rotation = 0) {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const local = [
    [-halfWidth, -halfDepth],
    [halfWidth, -halfDepth],
    [halfWidth, halfDepth],
    [-halfWidth, halfDepth],
  ];

  if (rotation === 0) return local.map(([x, z]) => [centerX + x, centerZ + z]);

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  return local.map(([x, z]) => [centerX + x * cos - z * sin, centerZ + x * sin + z * cos]);
}

/**
 * Corners of a regular polygon standing in for a circle or an ellipse.
 *
 * @param {number} rotation - Turns, not radians, so a caller can offset two adjacent rings by half a
 *   side without knowing how many sides there are.
 */
export function polygonFootprint(centerX, centerZ, radiusX, radiusZ, sides, rotation = 0) {
  const corners = [];

  for (let i = 0; i < sides; i += 1) {
    const angle = ((i + rotation) / sides) * Math.PI * 2;
    corners.push([centerX + Math.cos(angle) * radiusX, centerZ + Math.sin(angle) * radiusZ]);
  }

  return corners;
}

/**
 * A ring at a single height.
 *
 * @param {Array<[number, number]>} footprint
 * @param {number} y
 * @returns {{points: Array<[number, number, number]>}}
 */
export function ringAt(footprint, y) {
  return { points: footprint.map(([x, z]) => [x, y, z]) };
}

/**
 * A ring draped over the ground, one height per corner.
 *
 * Every solid's lowest ring uses this, so something standing on a slope has its footprint following the
 * terrain rather than floating at one corner and buried at another.
 */
export function groundRing(footprint, groundAt) {
  return { points: footprint.map(([x, z]) => [x, groundAt(x, z), z]) };
}

/**
 * Line segments for a stack of rings: each ring as a closed loop, plus the verticals joining them.
 *
 * An optional `apex` closes the stack to a point, which is what a cone or a tent peak is.
 *
 * @param {{rings: Array<{points: Array<[number, number, number]>}>, apex?: [number, number, number]}} solid
 * @param {number} baseY - Height the gradient measures from.
 * @param {number} height - Height the gradient measures over.
 * @returns {Array<object>}
 */
export function linesForSolid(solid, baseY, height) {
  const out = [];
  const t = (y) => fractionAt(y, baseY, height);

  for (const ring of solid.rings) {
    const { points } = ring;

    for (let i = 0; i < points.length; i += 1) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      if (samePoint(a, b)) continue;
      out.push(segmentAt(a[0], a[1], a[2], b[0], b[1], b[2], t(a[1]), t(b[1])));
    }
  }

  for (let r = 0; r < solid.rings.length - 1; r += 1) {
    const lower = solid.rings[r].points;
    const upper = solid.rings[r + 1].points;

    for (let i = 0; i < lower.length; i += 1) {
      const a = lower[i];
      const b = upper[i];
      if (samePoint(a, b)) continue;
      out.push(segmentAt(a[0], a[1], a[2], b[0], b[1], b[2], t(a[1]), t(b[1])));
    }
  }

  if (solid.apex) {
    const top = solid.rings.at(-1).points;
    const [ax, ay, az] = solid.apex;

    for (const point of top) {
      if (samePoint(point, solid.apex)) continue;
      out.push(segmentAt(point[0], point[1], point[2], ax, ay, az, t(point[1]), t(ay)));
    }
  }

  return out;
}

/**
 * Depth mask vertex and triangle counts for a list of ring stacks.
 *
 * Needed before allocating, because `tileContent` sizes one shared buffer for a whole tile.
 *
 * Per solid with R rings of N points: the lateral surface is (R-1) bands of N quads, and the top is closed
 * with a fan of N triangles — from an added centre vertex, or from the apex when the shape has one. Either
 * way the count is the same, which is why one formula serves both.
 *
 * **This has to agree with `emitSolidMask` exactly.** It is the one duplication the ring stacks cannot avoid:
 * the buffer is sized before anything is written, so the count cannot come from the writing. A test pins the
 * two together.
 *
 * @param {Array<object>} solids
 * @returns {{vertices: number, triangles: number}}
 */
export function countSolidsMask(solids) {
  let vertices = 0;
  let triangles = 0;

  for (const solid of solids) {
    const rings = solid.rings.length;
    const points = solid.rings[0].points.length;

    vertices += rings * points + 1;
    triangles += (rings - 1) * points * 2 + points;
  }

  return { vertices, triangles };
}
